/**
 * roomConfig.js — single source of truth for room-level selection definitions.
 *
 * Each entry in ROOM_ENABLED_LOCATIONS defines:
 *   - rooms: ordered list of rooms, each with:
 *       id          – unique slug used as the room key in selectionsBySection
 *       name        – display label
 *       requiresBasement – if true, hidden/disabled when basement is unfinished
 *       optionFilter – function(option) => boolean; which DB options apply here
 *
 * The key separator for room-based entries is ":" so they don't collide with
 * the existing "-" separator used by flat SelectionSection entries.
 * Example key: "flooringSelections:primary-bed"
 *
 * Locations NOT listed here remain flat (handled by SelectionSection).
 */

// ── Helper matchers ───────────────────────────────────────────────────────────

const nameIncludes = (...terms) => (opt) => {
  const n = (opt.optionname || opt.optionName || '').toLowerCase();
  return terms.some((t) => n.includes(t.toLowerCase()));
};


const allOptions = () => true;

// Flooring helpers
const isBasementCarpet = nameIncludes('(Basement)');
const isRegularCarpet  = (opt) => nameIncludes('Carpet')(opt) && !isBasementCarpet(opt);
const isCarpetPad      = nameIncludes('Carpet Pad');
const isBasementPad    = (opt) => isCarpetPad(opt) && isBasementCarpet(opt);
const isRegularPad     = (opt) => isCarpetPad(opt) && !isBasementCarpet(opt);
const isLVP            = nameIncludes('Luxury Vinyl', 'LVP');
const isTile           = nameIncludes('Tile');
const isHardSurface    = (opt) => isLVP(opt) || isTile(opt);

// Hard-surface rooms (entry, bath, kitchen): LVP or Tile
const hardSurfaceFilter = (opt) => isHardSurface(opt) && !isCarpetPad(opt);
// Bedroom rooms: regular carpet only (no basement variants, no pads)
const bedroomFilter = (opt) => isRegularCarpet(opt) && !isCarpetPad(opt);
// Basement bedroom rooms: basement carpet only (no pads)
const llBedroomFilter = (opt) => isBasementCarpet(opt) && !isBasementPad(opt);
// Whole-house LVP/carpet (living room, pantry, laundry): show both hard surface and carpet
const flexFilter = (opt) => (isHardSurface(opt) || isRegularCarpet(opt)) && !isCarpetPad(opt);
// Pad rooms (separate from the room selections)
const padFilter = (opt) => isRegularPad(opt);
const llPadFilter = (opt) => isBasementPad(opt);

// ── Room definitions ──────────────────────────────────────────────────────────

export const ROOM_ENABLED_LOCATIONS = {

  // ── FLOORING ──────────────────────────────────────────────────────────────
  Flooring: {
    rooms: [
      { id: 'garage-entry',  name: 'Garage Entry',    requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'front-entry',   name: 'Front Entry',     requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'living-room',   name: 'Living Room',     requiresBasement: false, optionFilter: flexFilter },
      { id: 'kitchen-dining',name: 'Kitchen / Dining',requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'pantry',        name: 'Pantry',          requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'primary-bath',  name: 'Primary Bath',    requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'primary-bed',   name: 'Primary Bedroom', requiresBasement: false, optionFilter: bedroomFilter },
      { id: 'main-bath',     name: 'Main Bath',       requiresBasement: false, optionFilter: hardSurfaceFilter },
      { id: 'bed-2',         name: '2nd Bedroom',     requiresBasement: false, optionFilter: bedroomFilter },
      { id: 'bed-3',         name: '3rd Bedroom',     requiresBasement: false, optionFilter: bedroomFilter },
      { id: 'laundry',       name: 'Laundry Room',    requiresBasement: false, optionFilter: flexFilter },
      { id: 'carpet-pad',    name: 'Carpet Pad',      requiresBasement: false, optionFilter: padFilter },
      { id: 'll-bedroom',    name: 'LL Bedroom',      requiresBasement: true,  optionFilter: llBedroomFilter },
      { id: 'll-bath',       name: 'LL Bath',         requiresBasement: true,  optionFilter: hardSurfaceFilter },
      { id: 'll-rec',        name: 'LL Rec Room',     requiresBasement: true,  optionFilter: llBedroomFilter },
      { id: 'll-pad',        name: 'LL Carpet Pad',   requiresBasement: true,  optionFilter: llPadFilter },
    ],
  },

  // ── CABINETS ──────────────────────────────────────────────────────────────
  Cabinets: {
    rooms: [
      {
        id: 'kitchen-perimeter',
        name: 'Kitchen Perimeter',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return (n.includes('kitchen') || n.includes('uppers') || n.includes('straight') || n.includes('trash'))
            && !n.includes('basement') && !n.includes('primary') && !n.includes('main bath')
            && !n.includes('laundry') && !n.includes('hardware');
        },
      },
      {
        id: 'kitchen-hardware',
        name: 'Kitchen Hardware',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('hardware') && (n.includes('kitchen') || (!n.includes('bath') && !n.includes('laundry') && !n.includes('basement')));
        },
      },
      {
        id: 'kitchen-soft-close',
        name: 'Kitchen Soft Close',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('soft close') && n.includes('kitchen');
        },
      },
      {
        id: 'under-cabinet-lighting',
        name: 'Under Cabinet Lighting',
        requiresBasement: false,
        optionFilter: nameIncludes('undercabinet', 'under-cabinet', 'no undercabinet'),
      },
      {
        id: 'primary-bath-cabinets',
        name: 'Primary Bath Cabinets',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('primary') && (n.includes('soft close') || n.includes('hardware'));
        },
      },
      {
        id: 'main-bath-cabinets',
        name: 'Main Bath Cabinets',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('main bath') && (n.includes('soft close') || n.includes('hardware'));
        },
      },
      {
        id: 'laundry-cabinets',
        name: 'Laundry Cabinets',
        requiresBasement: false,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('laundry');
        },
      },
      {
        id: 'll-bath-cabinets',
        name: 'LL Bath Cabinets',
        requiresBasement: true,
        optionFilter: (opt) => {
          const n = (opt.optionname || opt.optionName || '').toLowerCase();
          return n.includes('basement') && (n.includes('soft close') || n.includes('hardware'));
        },
      },
    ],
  },

  // ── COUNTERTOPS ───────────────────────────────────────────────────────────
  Countertops: {
    rooms: [
      {
        id: 'kitchen-perimeter',
        name: 'Kitchen Perimeter',
        requiresBasement: false,
        optionFilter: nameIncludes('Kitchen Perimeter'),
      },
      {
        id: 'kitchen-island',
        name: 'Kitchen Island',
        requiresBasement: false,
        optionFilter: nameIncludes('Kitchen Island'),
      },
      {
        id: 'kitchen-backsplash',
        name: 'Kitchen Backsplash',
        requiresBasement: false,
        optionFilter: nameIncludes('Kitchen Backsplash'),
      },
      {
        id: 'primary-bath',
        name: 'Primary Bath',
        requiresBasement: false,
        optionFilter: nameIncludes('Primary Bath'),
      },
      {
        id: 'main-bath',
        name: 'Main / Bath',
        requiresBasement: false,
        optionFilter: nameIncludes('Main/Basement'),
      },
      {
        id: 'll-bath',
        name: 'LL Bath',
        requiresBasement: true,
        optionFilter: nameIncludes('Main/Basement'),
      },
    ],
  },

  // ── LIGHTING ──────────────────────────────────────────────────────────────
  Lighting: {
    rooms: [
      { id: 'kitchen',       name: 'Kitchen',        requiresBasement: false, optionFilter: allOptions },
      { id: 'dining-room',   name: 'Dining Room',    requiresBasement: false, optionFilter: allOptions },
      { id: 'living-room',   name: 'Living Room',    requiresBasement: false, optionFilter: allOptions },
      { id: 'primary-bed',   name: 'Primary Bedroom',requiresBasement: false, optionFilter: allOptions },
      { id: 'bed-2',         name: '2nd Bedroom',    requiresBasement: false, optionFilter: allOptions },
      { id: 'bed-3',         name: '3rd Bedroom',    requiresBasement: false, optionFilter: allOptions },
      { id: 'exterior',      name: 'Exterior',       requiresBasement: false, optionFilter: allOptions },
      { id: 'll-bedroom',    name: 'LL Bedroom',     requiresBasement: true,  optionFilter: allOptions },
      { id: 'll-rec',        name: 'LL Rec Room',    requiresBasement: true,  optionFilter: allOptions },
    ],
  },
};

// Locations that use the flat SelectionSection (not room-based)
export const FLAT_LOCATIONS = new Set([
  'Elevation', 'Lot', 'Patio/Deck', 'Basement', 'Garage', 'Ceilings',
  'Windows',
  'Kitchen Backsplash', 'Kitchen Faucet', 'Sinks', 'Bathroom Fixtures', 'Shower', 'Toilets',
  'Appliances',
  'Mechanical',
  'Interior', 'Door Handles',
  'Fireplace',
  'Siding', 'Roof/Soffit', 'Porch', 'Stone', 'Front Door', 'Garage Door',
]);

/**
 * Returns true if this location should be displayed as room-based.
 */
export function isRoomEnabled(locationName) {
  return locationName in ROOM_ENABLED_LOCATIONS;
}

/**
 * Returns the room list for a given location, with basement-conditional rooms
 * filtered based on whether the basement is finished.
 */
export function getRooms(locationName, isBasementFinished) {
  const config = ROOM_ENABLED_LOCATIONS[locationName];
  if (!config) return [];
  return config.rooms.filter((room) => !room.requiresBasement || isBasementFinished);
}

/**
 * Returns the storage key for a room-based selection entry.
 * Uses ":" as separator to distinguish from flat "-" keys.
 */
export function roomSelectionKey(sectionKey, roomId) {
  return `${sectionKey}:${roomId}`;
}

/**
 * Given priceData.selectionsBySection, returns all room-keyed entries
 * for a given sectionKey.
 */
export function getRoomSelections(selectionsBySection, sectionKey) {
  const result = {};
  Object.entries(selectionsBySection || {}).forEach(([k, v]) => {
    if (k.startsWith(`${sectionKey}:`)) {
      const roomId = k.slice(sectionKey.length + 1);
      result[roomId] = Array.isArray(v) ? v : v ? [v] : [];
    }
  });
  return result;
}

/**
 * Option IDs that represent an "unfinished" basement (hide LL rooms).
 * Everything except opt-bsmt-0001 is considered finished.
 */
export const UNFINISHED_BASEMENT_OPTION_ID = 'opt-bsmt-0001';

/**
 * Given priceData, returns true if the customer has selected a finished basement.
 */
export function getIsBasementFinished(priceData) {
  const bySection = priceData?.selectionsBySection || {};
  // Look for any basementSelections entry
  const basementKeys = Object.keys(bySection).filter((k) => k.startsWith('basementSelections'));
  for (const key of basementKeys) {
    const ids = bySection[key];
    if (!ids || !ids.length) continue;
    // If any non-unfinished option is selected → finished
    const hasFinished = ids.some((id) => id !== UNFINISHED_BASEMENT_OPTION_ID);
    if (hasFinished) return true;
    // Explicitly unfinished
    if (ids.includes(UNFINISHED_BASEMENT_OPTION_ID)) return false;
  }
  // No basement selection yet — default to showing LL options (optimistic)
  return null;
}

/**
 * Given selectionsBySection, clears all LL room entries across all room-enabled locations.
 * Call this when the basement changes to unfinished.
 */
export function clearLLRoomSelections(selectionsBySection) {
  const updated = { ...selectionsBySection };
  Object.keys(updated).forEach((key) => {
    // key format: "{sectionKey}:{roomId}"
    if (!key.includes(':')) return;
    const roomId = key.split(':').pop();
    if (roomId.startsWith('ll-')) {
      delete updated[key];
    }
  });
  return updated;
}
